var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
import { findAllLifecycleMetadata, getSubscribeSwcAppMessageMetadata, getSubscribeSwcAppRouteChangeMetadata, ON_CONNECTED_SWC_APP_METADATA_KEY, SUBSCRIBE_SWC_APP_ROUTE_CHANGE_METADATA_KEY, buildSwcParameterArgs, getSwcRouteFieldMetadata, getSwcAppMessageFieldMetadata } from "../decorators/index.js";
import { SwcAppEngine } from "../SwcAppEngine.js";
import { debounceTimeIntervalLock, FunctionUtils, Subject, Observable } from "@dooboostore/core";
import { ValidUtils } from "@dooboostore/core-web";
import { SwcUtils } from "../utils/Utils.js";
const routeStateOf = (instance) => instance.__swc_routeState ?? (instance.__swc_routeState = /* @__PURE__ */ new Map());
const routeParamValues = (sp, pathDataAll) => {
  const queryAll = Object.fromEntries([...new Set(sp.keys())].map((k) => [k, sp.getAll(k)]));
  const pick = (all, at) => Object.fromEntries(Object.entries(all).map(([k, v]) => [k, at === "first" ? v[0] : v[v.length - 1]]));
  const lastOf = (v) => v && v.length ? v[v.length - 1] : void 0;
  const firstQuery = (k) => sp.get(k);
  const firstPath = (k) => pathDataAll[k]?.[0];
  return {
    swcAppRouteQueryParam: firstQuery,
    swcAppRouteFirstQueryParam: firstQuery,
    swcAppRouteLastQueryParam: (k) => lastOf(sp.getAll(k)) ?? null,
    swcAppRouteQueryParams: (k) => sp.getAll(k),
    swcAppRouteQueryParamObject: pick(queryAll, "first"),
    swcAppRouteFirstQueryParamObject: pick(queryAll, "first"),
    swcAppRouteLastQueryParamObject: pick(queryAll, "last"),
    swcAppRouteQueryParamsObject: queryAll,
    swcAppRouteURLSearchParams: sp,
    swcAppRoutePathVariable: firstPath,
    swcAppRouteFirstPathVariable: firstPath,
    swcAppRouteLastPathVariable: (k) => lastOf(pathDataAll[k]),
    swcAppRoutePathVariables: (k) => pathDataAll[k] ?? [],
    swcAppRoutePathVariableObject: pick(pathDataAll, "first"),
    swcAppRouteFirstPathVariableObject: pick(pathDataAll, "first"),
    swcAppRouteLastPathVariableObject: pick(pathDataAll, "last"),
    swcAppRoutePathVariablesObject: pathDataAll
  };
};
const matchRoute = (pathOption, instance, re) => {
  let pattern = typeof pathOption === "function" ? pathOption(instance) : pathOption;
  if (!pattern) return { pathData: {}, pathDataAll: {} };
  const path = re.path || "/";
  for (const p of Array.isArray(pattern) ? pattern : [pattern]) {
    const pathData = SwcUtils.parsePathPattern(p, path);
    if (pathData !== null) return { pathData, pathDataAll: SwcUtils.parsePathPatternAll(p, path) ?? {} };
  }
  return null;
};
const sameRouteValue = (a, b) => {
  if (a === b) return true;
  if (a instanceof URLSearchParams && b instanceof URLSearchParams) return a.toString() === b.toString();
  if (a && b && typeof a === "object" && typeof b === "object") return JSON.stringify(a) === JSON.stringify(b);
  return false;
};
const isSSR = (i) => {
  return i.hasAttribute("swc-use-ssr");
};
const setSSRAttribute = (i) => {
  i.setAttribute("swc-use-ssr", i._swcId ?? "");
};
const removeSSRAttribute = (i) => {
  if (isSSR(i)) i.__swc_ssrRoute ?? (i.__swc_ssrRoute = true);
  i.removeAttribute("swc-use-ssr");
};
function SwcAppMixin(Base) {
  class SwcAppMixinClass extends Base {
    constructor() {
      super(...arguments);
      __publicField(this, "__swc_engine", new SwcAppEngine(this));
      __publicField(this, "_swcId");
      __publicField(this, "_swc_connected_instance", /* @__PURE__ */ new Set());
      __publicField(this, "_routerSubscription");
      __publicField(this, "_lastRouterEvent");
      // replay 옵션으로 발행된 타입별 마지막 메시지 1개 보관
      __publicField(this, "_replayedMessages", /* @__PURE__ */ new Map());
      // 코드 구독(observeMessage)용 live 스트림. 데코레이터 구독과 같은 publishMessage에서 흘러나옴.
      __publicField(this, "_messageSubject", new Subject());
      __publicField(this, "_routeChangeInvocations", 0);
      __publicField(this, "_connectedInvocations", 0);
      __publicField(this, "_childrenRouteChangedInterval");
      __publicField(this, "_childrenConnectedDoneInterval");
      /**
       * Safari is 속성 처리
       * Safari polyfill에서는 자식부터 만들어지고 부모가 만들어지므로
       * 부모가 자식들을 강제로 등록해주는 형태로 처리
       */
      __publicField(this, "_connected_safari_and_standby", []);
      // connect() 전에 붙은 자식들이 기다리는 곳 — connect() 가 엔진을 띄운 뒤 깨운다
      __publicField(this, "_appReadyWaiters", []);
    }
    get simpleApplication() {
      return this.__swc_engine.simpleApplication;
    }
    get config() {
      return this.__swc_engine?.config;
    }
    get router() {
      return this.__swc_engine?.router;
    }
    // phase: 'live'(기본, 전체 방송) | 'connected' | 'connectedDone' (replay, 해당 trigger만)
    async _invokeRouteChangeSubscribers(instance, re, phase = "live") {
      if (instance.isConnected === false) return;
      if (phase !== "connectedDone") this._applyRouteFields(instance, re);
      this._routeChangeInvocations++;
      if (this._childrenRouteChangedInterval) {
        this.config.window.clearInterval(this._childrenRouteChangedInterval);
      }
      const intervalConfig = { setInterval: this.config.window.setInterval.bind(this.config.window), clearInterval: this.config.window.clearInterval.bind(this.config.window) };
      this._childrenRouteChangedInterval = debounceTimeIntervalLock(
        () => this._routeChangeInvocations === 0,
        () => {
          this._childrenRouteChangedInterval = null;
          this.config.onChildrenRouteChanged?.(re, this);
        },
        this.config?.childrenConnectedDoneCheckIntervalTime ?? 30,
        intervalConfig
      );
      try {
        const routeChangeSubscribers = getSubscribeSwcAppRouteChangeMetadata(instance);
        if (routeChangeSubscribers.length > 0) {
          const state = routeStateOf(instance);
          const routeSig = JSON.stringify([re.path ?? "", re.search ?? ""]);
          if (ValidUtils.isBrowser() && isSSR(instance)) instance.__swc_ssrRoute ?? (instance.__swc_ssrRoute = true);
          if (instance.__swc_ssrRoute === true) instance.__swc_ssrRoute = routeSig;
          const ssrPainted = instance.__swc_ssrRoute === routeSig;
          if (!ssrPainted) delete instance.__swc_ssrRoute;
          let stopped = false;
          for (const metadata of routeChangeSubscribers) {
            if (phase !== "live" && (metadata.options?.trigger ?? "connected") !== phase) continue;
            const on = metadata.options?.on ?? "match";
            if (on === "beforeLeave") continue;
            const methodName = metadata.propertyKey;
            const extractValue = (v) => {
              const keyToUse = metadata.options?.valueKey ?? SUBSCRIBE_SWC_APP_ROUTE_CHANGE_METADATA_KEY;
              if (v && typeof v === "object" && keyToUse in v) {
                return v[keyToUse];
              }
              return v;
            };
            const match = matchRoute(metadata.options?.path, instance, re);
            const prev = state.get(methodName);
            const signature = match ? JSON.stringify([re.path ?? "", re.search ?? ""]) : "";
            state.set(methodName, match ? { matched: true, signature, pathData: match.pathData, pathDataAll: match.pathDataAll } : { matched: false, signature: "" });
            const shouldRun = on === "match" ? !!match : on === "enter" ? !!match && !prev?.matched : on === "update" ? !!match && !!prev?.matched && prev.signature !== signature : on === "leave" ? !match && !!prev?.matched : false;
            if (stopped || ssrPainted || !shouldRun || !instance[methodName]) continue;
            const pathData = match ? match.pathData : prev?.pathData ?? {};
            const pathDataAll = match ? match.pathDataAll : prev?.pathDataAll ?? {};
            const filter = metadata.options?.filter;
            const instanceHelperHostSet = SwcUtils.getHelperAndHostSet(instance, this.config.window);
            const filterPassed = !filter || await filter(this.router, { helper: instanceHelperHostSet, currentThis: instance });
            if (!filterPassed) continue;
            const before = metadata.options?.before;
            const finaly = metadata.options?.finally;
            const beforeReturn = before ? await before(this.router, { helper: instanceHelperHostSet, currentThis: instance }) : void 0;
            const routeEventValue = { ...re, pathData };
            const args = this._buildRouteArgs(instance, methodName, routeEventValue, pathDataAll, beforeReturn);
            let rawResult, error;
            try {
              rawResult = await instance[methodName](...args);
            } catch (e) {
              error = e;
            } finally {
              if (finaly) await finaly(this.router, { helper: instanceHelperHostSet, currentThis: instance }, { args, result: rawResult, error });
            }
            if (error) throw error;
            const result = extractValue(rawResult);
            if (result !== void 0 && result !== null) {
              stopped = true;
            }
          }
        }
      } finally {
        const isBrowser = ValidUtils.isBrowser();
        if (!isBrowser && this.config?.ssr) {
          setSSRAttribute(instance);
        } else {
          removeSSRAttribute(instance);
        }
        this._routeChangeInvocations = Math.max(0, this._routeChangeInvocations - 1);
      }
    }
    /**
     * 필드에 붙은 라우트 파라미터 데코레이터(@swcAppRouteQueryParam('tag') tag, @swcAppRoutePathVariable('id', { path }) id …)에
     * 지금 라우트 값을 넣는다. query 는 URL 에서 바로, path 변수는 필드의 path 패턴에 매칭해서 (안 맞으면 비어 있음 — 핸들러 인자와 같은 규칙).
     */
    _applyRouteFields(instance, re) {
      const fields = getSwcRouteFieldMetadata(instance);
      if (!fields.length) return;
      const sp = re.searchParams ?? new URLSearchParams(re.search ?? "");
      const queryValues = routeParamValues(sp, {});
      for (const field of fields) {
        const values = field.kind.includes("PathVariable") ? routeParamValues(sp, matchRoute(field.path, instance, re)?.pathDataAll ?? {}) : queryValues;
        const resolver = values[field.kind];
        const value = field.key !== void 0 && typeof resolver === "function" ? resolver(field.key) : resolver;
        if (!sameRouteValue(instance[field.propertyKey], value)) instance[field.propertyKey] = value;
      }
    }
    /** 라우트 핸들러 인자: @swcAppRouterEvent / host 계열 / @routeChangeBeforeReturn / 라우트 파라미터(query·path 변수) */
    _buildRouteArgs(instance, methodName, routeEventValue, pathDataAll, beforeReturn) {
      const instanceHelperHostSet = SwcUtils.getHelperAndHostSet(instance, this.config.window);
      const sp = routeEventValue.searchParams ?? new URLSearchParams(routeEventValue.search ?? "");
      return buildSwcParameterArgs(
        instance,
        methodName,
        {
          swcAppRouterEvent: routeEventValue,
          swcAppRouter: this.router,
          swcAppSimpleApplication: this.simpleApplication,
          swcAppHost: this,
          hostSet: instanceHelperHostSet,
          helperHostSet: instanceHelperHostSet,
          helperSet: SwcUtils.getHelperSet(this.config.window),
          routeChangeBeforeReturn: beforeReturn,
          ...routeParamValues(sp, pathDataAll)
        },
        [routeEventValue]
      );
    }
    /**
     * on: 'beforeLeave' 구독을 라우터 가드로 등록. 지금 경로는 맞고 다음 경로는 안 맞을 때 핸들러를 부르고,
     * false 를 리턴하면 이동을 취소한다. 요소가 떨어지면 _disconnected 에서 해제.
     */
    _registerLeaveGuards(instance) {
      if (!this.router?.addLeaveGuard) return;
      instance.__swc_leaveGuardOffs?.forEach((off) => off());
      instance.__swc_leaveGuardOffs = getSubscribeSwcAppRouteChangeMetadata(instance).filter((m) => m.options?.on === "beforeLeave").map(
        (metadata) => this.router.addLeaveGuard(async (to, from) => {
          if (instance.isConnected === false || !instance[metadata.propertyKey]) return true;
          const fromMatch = matchRoute(metadata.options?.path, instance, from);
          if (!fromMatch || matchRoute(metadata.options?.path, instance, to)) return true;
          const filter = metadata.options?.filter;
          const helper = SwcUtils.getHelperAndHostSet(instance, this.config.window);
          if (filter && !await filter(this.router, { helper, currentThis: instance })) return true;
          const beforeReturn = metadata.options?.before ? await metadata.options.before(this.router, { helper, currentThis: instance }) : void 0;
          const routeEventValue = { ...from, pathData: fromMatch.pathData, to };
          const args = this._buildRouteArgs(instance, metadata.propertyKey, routeEventValue, fromMatch.pathDataAll, beforeReturn);
          let result, error;
          try {
            result = await instance[metadata.propertyKey](...args);
          } catch (e) {
            error = e;
          } finally {
            if (metadata.options?.finally) await metadata.options.finally(this.router, { helper, currentThis: instance }, { args, result, error });
          }
          if (error) throw error;
          return result !== false;
        })
      );
    }
    async _connected(instance, option) {
      if (instance) {
        if (!this.simpleApplication && !this.config) {
          if (instance === this) {
            this._connected_safari_and_standby.push(instance);
            return;
          }
          await new Promise((resolve) => this._appReadyWaiters.push(resolve));
        }
        this.config.onConnectedChildBefore?.(instance);
        if (!option?.noIncrements) {
          this._connectedInvocations++;
        }
        this._swc_connected_instance.add(instance);
        const gen = instance.__swc_connectGen;
        const stale = () => instance.__swc_connectGen !== gen;
        const connectedSwcAppCallBacks = findAllLifecycleMetadata(instance, ON_CONNECTED_SWC_APP_METADATA_KEY);
        for (let connectedSwcAppCallBack of connectedSwcAppCallBacks) {
          await instance?._invokeLifecycleMethod?.(connectedSwcAppCallBack.propertyKey);
          if (stale()) return this;
        }
        if (this._childrenConnectedDoneInterval) {
          this.config.window.clearInterval(this._childrenConnectedDoneInterval);
        }
        const intervalConfig = { setInterval: this.config.window.setInterval.bind(this.config.window), clearInterval: this.config.window.clearInterval.bind(this.config.window) };
        this._childrenConnectedDoneInterval = debounceTimeIntervalLock(
          () => this._connectedInvocations === 0,
          () => {
            this._childrenConnectedDoneInterval = null;
            this.config.onChildrenConnectedDone?.(this);
          },
          this.config?.childrenConnectedDoneCheckIntervalTime ?? 30,
          intervalConfig
        );
        if (this.simpleApplication) {
          try {
            if (this.router && this._lastRouterEvent) {
              await this._invokeRouteChangeSubscribers(instance, this._lastRouterEvent, "connected");
            }
          } catch (e) {
            throw e;
          }
        }
        if (stale()) return this;
        if (this.simpleApplication) this._registerLeaveGuards(instance);
        this._replayMessageFieldsTo(instance);
        this._replayMessagesTo(instance);
        const isBrowser = ValidUtils.isBrowser();
        if (!isBrowser && this.config?.ssr) {
          setSSRAttribute(instance);
        } else {
          removeSSRAttribute(instance);
        }
      }
      this.config.onConnectedChildAfter?.(instance);
      return this;
    }
    async _connectedDone(instance, option) {
      try {
        if (option?.aborted) return;
        this._replayMessagesTo(instance, "connectedDone");
        if (this.simpleApplication) {
          try {
            if (this.router && this._lastRouterEvent) {
              await this._invokeRouteChangeSubscribers(instance, this._lastRouterEvent, "connectedDone");
            }
          } catch (e) {
            throw e;
          }
        }
      } finally {
        this._connectedInvocations = Math.max(0, this._connectedInvocations - 1);
      }
    }
    _disconnected(instance) {
      if (instance) {
        this._swc_connected_instance.delete(instance);
        instance.__swc_leaveGuardOffs?.forEach((off) => off());
        instance.__swc_leaveGuardOffs = void 0;
        instance.__swc_routeState = void 0;
        this.config?.onDisconnectedChildAfter?.(instance);
      }
    }
    async _handleRouteChange(route) {
      this._lastRouterEvent = route;
      const swcConnectedInstance = Array.from(this._swc_connected_instance);
      const ps = swcConnectedInstance.map((instance) => this._invokeRouteChangeSubscribers(instance, route));
      await Promise.allSettled(ps);
    }
    async connect(config) {
      const swcConfig = {
        routeType: "element",
        window: config?.window,
        ...config
      };
      await this.__swc_engine.connect(swcConfig);
      this._appReadyWaiters.splice(0).forEach((resolve) => resolve());
      this._connected_safari_and_standby.forEach((instance) => {
        this._connected(instance, { noIncrements: true });
      });
      if (!this._routerSubscription && this.router) {
        this._routerSubscription = this.router.observable.subscribe(async (route) => {
          if (route.triggerPoint === "end") {
            await this._handleRouteChange(route);
          }
        });
      }
      await this._invokeHook("onSwcAppConnected");
    }
    connectedCallback() {
      super.connectedCallback?.();
      const configStr = this.getAttribute("swc-get-application-config");
      const win = this.ownerDocument?.defaultView || window;
      if (configStr && win) {
        try {
          const userConfig = FunctionUtils.executeReturn({
            script: configStr,
            context: this,
            args: SwcUtils.getHelperAndHostSet(this, win)
          });
          if (userConfig instanceof Promise) {
            userConfig.then((resolvedConfig) => {
              this.connect(resolvedConfig);
            }).catch((e) => {
              console.error("[SWC-MIXIN] Script execution failed:", e);
            });
          } else {
            this.connect(userConfig);
          }
        } catch (e) {
          console.error("[SWC-MIXIN] Script execution failed:", e);
        }
      }
      this._connected(this);
      this.__swc_engine?.config?.onConnected?.(this);
      this._connectedDone(this);
      void this._invokeHook("onConnected").catch((e) => console.error("[SWC-MIXIN] onConnected error:", e));
    }
    disconnectedCallback() {
      super.disconnectedCallback?.();
      this._routerSubscription?.unsubscribe();
      this._routerSubscription = void 0;
      this._lastRouterEvent = void 0;
      this.__swc_engine.disconnect();
      this.__swc_engine?.config?.onDisconnected?.(this);
      this._disconnected(this);
      void this._invokeHook("onDisconnected").catch((e) => console.error("[SWC-MIXIN] onDisconnected error:", e));
    }
    async routing(path) {
      await this.router?.go(path);
    }
    back() {
      this.router?.back();
    }
    forward() {
      this.router?.forward();
    }
    reload() {
      this.router?.reload();
    }
    publishMessage(message) {
      if (message.type) {
        const buf = [...this._replayedMessages.get(message.type) ?? [], message];
        this._replayedMessages.set(message.type, buf.slice(-Math.max(1, this.config?.messageReplayBufferSize ?? 10)));
      }
      this._swc_connected_instance.forEach((instance) => {
        this._applyMessageFields(instance, message);
        this._invokeMessageSubscribers(instance, message);
      });
      this._messageSubject.next(message);
    }
    connectedElements() {
      return Array.from(this._swc_connected_instance);
    }
    observeMessage(typeOrOptions, options) {
      const { type, subject } = typeof typeOrOptions === "string" ? { ...options, type: typeOrOptions } : { ...typeOrOptions };
      return new Observable((subscriber) => {
        if (type && (subject === "behavior" || subject === "replay")) {
          const buf = this._replayedMessages.get(type) ?? [];
          (subject === "behavior" ? buf.slice(-1) : buf).forEach((m) => subscriber.next(m));
        }
        const sub = this._messageSubject.subscribe((m) => {
          if (!type || m.type === type) subscriber.next(m);
        });
        return () => sub.unsubscribe();
      });
    }
    // hook 실행 + @inject 파라미터 DI 해결 (host 본체 포함.
    // _invokeLifecycleMethod는 host/hostSet 상황 분기가 달라 호스트 본체에서 주입이 안 됨)
    async _invokeHook(methodName) {
      const fn = this[methodName];
      if (typeof fn !== "function") return;
      const manager = this.simpleApplication?.simstanceManager;
      if (!manager) {
        return fn.call(this);
      }
      return manager.executeBindParameterSimPromise({ target: this, targetKey: methodName });
    }
    /** 필드에 붙은 @swcAppMessage — 타입이 맞으면(타입 없으면 전부) 그 필드에 메시지를 넣는다 */
    _applyMessageFields(instance, message, fields = getSwcAppMessageFieldMetadata(instance)) {
      for (const field of fields) {
        if (!field.messageType || field.messageType === message.type) instance[field.propertyKey] = message;
      }
    }
    /**
     * 새로 붙은 인스턴스의 @swcAppMessage 필드를 버퍼로 채운다 (렌더 전). @subscribeSwcAppMessage 와 같은 규칙:
     * 'behavior' = 마지막 1개, 'replay' = 버퍼 전체 시간순(결국 마지막 값), 미지정·'subject' = 채우지 않음. 타입이 있을 때만.
     */
    _replayMessageFieldsTo(instance) {
      for (const field of getSwcAppMessageFieldMetadata(instance)) {
        if (!field.messageType || field.subject !== "behavior" && field.subject !== "replay") continue;
        const buf = this._replayedMessages.get(field.messageType) ?? [];
        for (const message of field.subject === "behavior" ? buf.slice(-1) : buf) this._applyMessageFields(instance, message, [field]);
      }
    }
    // 신규 연결 인스턴스 1곳에만 버퍼 재생. 구독자별 subject 선언 기준:
    // - 'behavior': 마지막 1개 / 'replay': 버퍼 전체 시간순 / 미지정·'subject': live만 (재생 안 함)
    // - trigger 'connectedDone'인 구독은 _connectedDone(render 완료 후)에서 재생. phase로 구분.
    // 이미 연결된 인스턴스는 publishMessage 때 live로 받으므로 여기 오지 않음.
    _replayMessagesTo(instance, phase = "connected") {
      const subs = getSubscribeSwcAppMessageMetadata(instance);
      if (!subs || !Array.isArray(subs)) return;
      const seen = /* @__PURE__ */ new Set();
      subs.forEach((meta) => {
        const type = meta.messageType;
        if (!type || seen.has(type)) return;
        seen.add(type);
        if ((meta.trigger ?? "connected") !== phase) return;
        const buf = this._replayedMessages.get(type) ?? [];
        if (meta.subject === "behavior") {
          const last = buf[buf.length - 1];
          if (last) this._invokeMessageSubscribers(instance, last);
        } else if (meta.subject === "replay") {
          buf.forEach((msg) => this._invokeMessageSubscribers(instance, msg));
        }
      });
    }
    _invokeMessageSubscribers(instance, message) {
      const messageSubscribers = getSubscribeSwcAppMessageMetadata(instance);
      if (messageSubscribers && Array.isArray(messageSubscribers)) {
        messageSubscribers.forEach((metadata) => {
          void this._runMessageSubscriber(instance, message, metadata).catch((e) => console.error("[SWC] subscribeSwcAppMessage error:", e));
        });
      }
    }
    async _runMessageSubscriber(instance, message, metadata) {
      const methodName = metadata.propertyKey;
      const messageType = metadata.messageType;
      const { filter, before, finally: finaly } = metadata;
      const typeMatched = !messageType || message.type === messageType;
      if (!typeMatched) return;
      if (filter && !await filter(message, instance)) return;
      if (typeof instance[methodName] !== "function") return;
      const instanceHelperHostSet = SwcUtils.getHelperAndHostSet(instance, this.config.window);
      const instanceHelperSet = SwcUtils.getHelperSet(this.config.window);
      const beforeReturn = before ? await before(message, instance) : void 0;
      const args = buildSwcParameterArgs(
        instance,
        methodName,
        {
          swcAppMessage: message,
          swcAppRouter: this.router,
          swcAppSimpleApplication: this.simpleApplication,
          swcAppHost: this,
          hostSet: instanceHelperHostSet,
          helperHostSet: instanceHelperHostSet,
          helperSet: instanceHelperSet,
          swcAppMessageBeforeReturn: beforeReturn
        },
        [message]
      );
      let result, error;
      try {
        result = await instance[methodName](...args);
      } catch (e) {
        error = e;
      } finally {
        if (finaly) await finaly(message, instance, { args, result, error });
      }
      if (error) throw error;
    }
  }
  return SwcAppMixinClass;
}
export {
  SwcAppMixin,
  isSSR,
  removeSSRAttribute,
  setSSRAttribute
};
//# sourceMappingURL=SwcAppMixin.js.map
